import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS } from "@/config/site";
import api from "@/lib/api";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="brand-header-logo-link" aria-label="STJ Innovations home" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.dropdown ? (
              <div key={l.to} className="group relative">
                <NavLink
                  to={l.to}
                  data-testid={l.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-[13.5px] font-medium tracking-wide transition-colors ${isActive ? "text-accent" : "text-ink hover:text-accent"}`
                  }
                >
                  {l.label}
                  <ChevronDown size={13} className="transition-transform group-hover:rotate-180" />
                </NavLink>
                <div className="invisible absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="border border-line bg-white p-6 shadow-[0_24px_60px_-20px_rgba(8,21,36,0.25)]">
                    <p className="eyebrow mb-4">Product Categories</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                      {categories.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/products/category/${c.slug}`}
                          data-testid={`nav-category-${c.slug}`}
                          className="group/item flex items-center justify-between border-b border-line/70 py-2.5 text-[13px] text-ink transition-colors hover:text-accent"
                        >
                          {c.name}
                          <ArrowRight size={12} className="text-line transition-all group-hover/item:translate-x-0.5 group-hover/item:text-accent" />
                        </Link>
                      ))}
                    </div>
                    <Link to="/products" data-testid="nav-all-products" className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-accent hover:text-accent-dark">
                      View all products <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `text-[13.5px] font-medium tracking-wide transition-colors ${isActive ? "text-accent" : "text-ink hover:text-accent"}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/request-a-quote"
            data-testid="header-request-quote-button"
            className="group hidden items-center gap-2 bg-accent px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-accent-dark sm:inline-flex"
          >
            Request a Quote
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="inline-flex h-10 w-10 items-center justify-center border border-line text-ink lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-line bg-white lg:hidden" data-testid="mobile-menu">
          <nav className="mx-auto max-w-7xl px-4 py-6 sm:px-6" aria-label="Mobile">
            <ul className="space-y-1">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`mobile-${l.testId}`}
                    className="flex items-center justify-between border-b border-line/70 py-3.5 font-display text-lg font-medium text-ink"
                  >
                    {l.label}
                    <ArrowRight size={16} className="text-steel" />
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/request-a-quote"
              data-testid="mobile-request-quote-button"
              className="mt-6 flex w-full items-center justify-center gap-2 bg-accent px-5 py-3.5 text-sm font-semibold text-white"
            >
              Request a Quote <ArrowRight size={15} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
